import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  BookmarkSquareIcon,
  CircleStackIcon,
  MagnifyingGlassIcon,
  StarIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userAppContext, searchContext } from '../../../../../../context/Context';
import { SearchContext, UserContext } from '../../../../../../types';
import { Filter, SavedSearch } from '../../../../../../components/ProfileArea/types';
import { useRightSidebarLayout } from '../../../context/RightSidebarLayoutContext';
import { SidebarColumns } from './SidebarColumns';
import { SidebarSources } from './SidebarSources';
import useScrollWindow from '@/hooks/useScrollWindow';
import { chemicalRestrictedColumns, defaultModel } from '../../../constants';
import { MappingContent } from '../types';
import { ColumnVisibilityModels } from '@/components/Table/ColumnVisibilityModels';
import { useMappingStackStore } from '@/store';
import { getSourceList } from '../../../helpers/sourceListHelpers';
import { SearchItemsPanelContent } from './SearchItemsPanelContent';
import { MyScoreInlineContent } from './MyScoreInlineContent';
import { FavoritesPanelContent } from './FavoritesPanelContent';
import { SavedColumnsPanelContent } from './SavedColumnsPanelContent';
import { useMyScoreDraft } from './useMyScoreDraft';
import { useSidebarFavorites } from './useSidebarFavorites';
import {
  buildSearchUrl,
  getSearchPlaceholderAndSuggestions,
  normalizeDataset,
} from './rightAsideSidebar.utils';
import { RightAsideSidebarView } from './RightAsideSidebarView';
import { searchTabHasGraphs } from '../../../helpers/graphTabVisibility';

export interface RightAsideSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  source: string;
  sourceWeights: Array<{ name: string; score: number }>;
  view: string;
  tab: string;
  isEvidence?: boolean;
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  searchItems?: any[];
  onOpenSearchItemInfo?: (item: any) => void;
  onRemoveSearchItem?: (index: number) => void;
  onClearAllSearchItems?: () => void;
  isMapping?: boolean;
  columnPanelToggle?: { active: 'summary' | 'evidence'; onChange: (active: 'summary' | 'evidence') => void };
  showMyScoreSection?: boolean;
  showSavedColumnsSection?: boolean;
  onApplySavedColumns?: (columns: any) => void;
  onDeleteSavedColumns?: (id: string) => void;
  onOpenScoreModal?: () => void;
  onApplySavedScore?: (score: any) => void;
  onDeleteSavedScore?: (id: string) => void;
  onClearWeights?: () => void;
  hasActiveWeights?: boolean;
  refreshScoresDropdown?: number;
  mappingContent?: MappingContent;
  isAcademic?: boolean;
  columnOrder?: string[];
  setColumnOrder?: (order: string[]) => void;
  onResetToDefault?: () => void;
  demoTourActive?: boolean;
}

export function RightAsideSidebar({
  isOpen,
  onClose,
  source,
  sourceWeights,
  view,
  tab,
  isEvidence = false,
  columnVisibilityModel,
  setColumnVisibilityModel,
  searchItems = [],
  onOpenSearchItemInfo,
  onRemoveSearchItem,
  onClearAllSearchItems,
  isMapping = false,
  columnPanelToggle,
  showMyScoreSection = false,
  onApplySavedColumns,
  onOpenScoreModal,
  onApplySavedScore,
  onClearWeights,
  mappingContent,
  isAcademic,
  columnOrder,
  setColumnOrder,
  onResetToDefault,
  demoTourActive = false,
}: RightAsideSidebarProps) {
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { isAuthenticated, restrictGraphDisplay, groups } = userAppContext() as unknown as UserContext;
  const isTrial = groups?.includes('ROLE_TRIAL');
  const canViewAnalyticsGraphs = !restrictGraphDisplay || isTrial;
  const searchContextValue = searchContext() as unknown as SearchContext;
  const { setSearchItems: contextSetSearchItems, searchItems: contextSearchItems } = searchContextValue ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSidebarOpen, searchItemsSectionExpandSignal } = useRightSidebarLayout();
  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;

  const { favorites, favoritesLoading, savedColumns, activeSavedColumnsId, setActiveSavedColumnsId } =
    useSidebarFavorites(isAuthenticated, isOpen);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [, setSidebarDropdownOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<any[]>(contextSearchItems ?? []);
  const [activeCollapsedPanel, setActiveCollapsedPanel] = useState<string | null>(null);
  const [pinnedCollapsedPanel, setPinnedCollapsedPanel] = useState<string | null>(null);
  const collapsedHoverCloseTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setPendingItems(contextSearchItems ?? []);
  }, [contextSearchItems]);

  const mappingTabSegment = (mappingContent as any)?.tabSegment;
  const collapseMappingToRoot = useMappingStackStore(s => s.collapseToRootLevel);
  const effectiveSidebarTab =
    isMapping && typeof mappingTabSegment === 'string' && mappingTabSegment.length > 0
      ? mappingTabSegment.toUpperCase()
      : tab;
  const selectedView: 'tables' | 'graphs' = searchParams.get('display') === 'graphs' ? 'graphs' : 'tables';
  const restrictSources = (searchContextValue as any)?.restrictSources;

  const availableScoreSources = useMemo(() => {
    if (!tab) return [];
    const sourceList = getSourceList({ tab, restrictSources });
    const flat = Array.isArray(sourceList) ? sourceList : Object.values(sourceList || {}).flat();
    return [...new Set(flat.filter(sourceName => sourceName !== 'ALL'))];
  }, [tab, restrictSources]);

  const {
    myScoreThreshold,
    myScoreDraft,
    isMyScoreSourcePickerOpen,
    setIsMyScoreSourcePickerOpen,
    thresholdAdjustedSources,
    myScoreLimitAdjusted,
    availableSourcesToAdd,
    handleMyScoreSliderChange,
    handleApplyMyScoreDraft,
    handleAddMyScoreSource,
    handleRemoveMyScoreSource,
    handleThresholdChange,
    getSliderTrackStyle,
  } = useMyScoreDraft({ tab, sourceWeights, isOpen, onApplySavedScore });

  useEffect(() => {
    if (isOpen || !isDesktop) {
      setPinnedCollapsedPanel(null);
      setActiveCollapsedPanel(null);
    }
  }, [isOpen, isDesktop]);

  useEffect(
    () => () => {
      if (collapsedHoverCloseTimeoutRef.current !== null) {
        window.clearTimeout(collapsedHoverCloseTimeoutRef.current);
      }
    },
    [],
  );

  const clearCollapsedHoverCloseTimeout = () => {
    if (collapsedHoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(collapsedHoverCloseTimeoutRef.current);
      collapsedHoverCloseTimeoutRef.current = null;
    }
  };
  const scheduleCollapsedFlyoutClose = (sectionId: string) => {
    clearCollapsedHoverCloseTimeout();
    collapsedHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setActiveCollapsedPanel(prev =>
        prev === sectionId && pinnedCollapsedPanel !== sectionId ? null : prev,
      );
      collapsedHoverCloseTimeoutRef.current = null;
    }, 140);
  };

  const handleViewChange = (nextView: 'tables' | 'graphs') => {
    if (selectedView === nextView) return;
    if (selectedView === 'tables' && nextView === 'graphs') collapseMappingToRoot();
    setSearchParams(
      prev => {
        const updated = new URLSearchParams(prev);
        if (nextView === 'tables') updated.delete('display');
        else updated.set('display', nextView);
        return updated;
      },
      { replace: true },
    );
  };

  const handleFavoriteClick = (search: SavedSearch) => {
    if (search.type === 'score' && search.searchContext && search.sourceWeights) {
      const { view: v, tab: t, source: s, searchItems: ctxItems } = search.searchContext;
      const url = new URL(`${window.location.origin}/search`);
      url.searchParams.set('view', v || 'ALL');
      url.searchParams.set('tab', t || 'GDA');
      url.searchParams.set('source', search.sourceWeights.length > 1 ? 'ALL' : s || 'ALL');
      url.searchParams.set('idents', ctxItems?.length ? ctxItems.map(item => item.id).join(',') : 'ALL');
      sessionStorage.setItem(`sourceWeights_${t}`, JSON.stringify(search.sourceWeights));
      navigate(url.href.replace(window.location.origin, ''));
      onClose();
      return;
    }

    if ((search.type === 'columns' || search.type === 'complete') && search.searchContext) {
      if (search.type === 'complete' && search.searchQuery) {
        const targetUrl = buildSearchUrl(search);
        if (!targetUrl) return;
        const url = new URL(targetUrl);
        url.searchParams.set('loadComplete', search.id);
        navigate(url.href.replace(window.location.origin, ''));
        onClose();
        return;
      }

      const { view: v, tab: t, source: s, searchItems: items } = search.searchContext;
      const url = new URL(`${window.location.origin}/search`);
      url.searchParams.set('view', v || 'ALL');
      url.searchParams.set('tab', t || 'GDA');
      url.searchParams.set('source', s || 'ALL');
      url.searchParams.set('idents', items?.length ? items.map(item => item.id).join('-') : 'ALL');
      if (search.searchContext.isEvidence !== undefined) {
        url.searchParams.set('activePanel', search.searchContext.isEvidence ? 'evidence' : 'summary');
        url.searchParams.set('isEvidence', String(search.searchContext.isEvidence));
      }
      url.searchParams.set('loadColumns', search.id);
      navigate(url.href.replace(window.location.origin, ''));
      onClose();
      return;
    }

    const targetUrl = search.searchQuery
      ? buildSearchUrl(search)
      : buildSearchUrl({
          ...search,
          filters: (search.filters || []).map((f: Filter) => ({
            ...f,
            isEvidence:
              f.isEvidence !== undefined
                ? f.isEvidence
                : (search.searchContext?.isEvidence ?? search.table === 'Evidence'),
          })),
        });
    if (!targetUrl) return;
    const url = new URL(targetUrl);
    if (search.type === 'complete' && search.searchQuery) url.searchParams.set('loadComplete', search.id);
    navigate(url.href.replace(window.location.origin, ''));
    onClose();
  };

  const shouldShowResetButton = () => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    const restrictedList = isAuthenticated && isAcademic ? chemicalRestrictedColumns : [];
    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);
    const hasCustomVisibility =
      !!columnVisibilityModel &&
      !!computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);
    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];
    const hasCustomOrder =
      !!columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);
    return hasCustomVisibility || hasCustomOrder;
  };

  const showManageGraph = searchTabHasGraphs(view as string, tab as string, {
    searchItemCount: searchItems?.length ?? 0,
  });
  const viewFromUrl = normalizeDataset(searchParams.get('view') ?? view);
  const activeSearchDataset =
    normalizeDataset(searchItems.length > 0 ? searchItems[0]?.dataset : undefined) ?? viewFromUrl;
  const { searchPlaceholder, searchSuggestions } = getSearchPlaceholderAndSuggestions(activeSearchDataset);
  const sourcesHeaderBadge =
    sourceWeights.length > 0 ? sourceWeights.length : source !== 'ALL' ? 1 : undefined;

  const searchItemsSection = (
    <SearchItemsPanelContent
      contextSetSearchItems={contextSetSearchItems}
      searchInputValue={searchInputValue}
      setSearchInputValue={setSearchInputValue}
      pendingItems={pendingItems}
      setPendingItems={setPendingItems}
      setSidebarDropdownOpen={setSidebarDropdownOpen}
      activeSearchDataset={activeSearchDataset}
      searchItems={searchItems}
      isMapping={isMapping}
      onOpenSearchItemInfo={onOpenSearchItemInfo}
      onRemoveSearchItem={onRemoveSearchItem}
      onClearAllSearchItems={onClearAllSearchItems}
      searchSuggestions={searchSuggestions}
      searchPlaceholder={searchPlaceholder}
    />
  );

  const myScoreInlineContent = (
    <MyScoreInlineContent
      myScoreDraft={myScoreDraft}
      myScoreThreshold={myScoreThreshold}
      isMyScoreSourcePickerOpen={isMyScoreSourcePickerOpen}
      availableSourcesToAdd={availableSourcesToAdd(availableScoreSources)}
      thresholdAdjustedSources={thresholdAdjustedSources}
      myScoreLimitAdjusted={myScoreLimitAdjusted}
      onToggleSourcePicker={() => setIsMyScoreSourcePickerOpen(prev => !prev)}
      onAddSource={handleAddMyScoreSource}
      onRemoveSource={handleRemoveMyScoreSource}
      onSliderChange={handleMyScoreSliderChange}
      onThresholdChange={handleThresholdChange}
      onApply={handleApplyMyScoreDraft}
      onClearWeights={onClearWeights}
      getSliderTrackStyle={getSliderTrackStyle}
    />
  );

  const favoritesSection = (
    <FavoritesPanelContent
      isAuthenticated={isAuthenticated}
      favoritesLoading={favoritesLoading}
      favorites={favorites}
      onFavoriteClick={handleFavoriteClick}
    />
  );
  const savedColumnsSection = (
    <SavedColumnsPanelContent
      isAuthenticated={isAuthenticated}
      savedColumns={savedColumns}
      activeSavedColumnsId={activeSavedColumnsId}
      setActiveSavedColumnsId={setActiveSavedColumnsId}
      onApplySavedColumns={onApplySavedColumns}
    />
  );

  const collapsedSections = [
    ...(searchItems.length > 0 || contextSetSearchItems
      ? [
          {
            id: 'search-items',
            title: 'Search items',
            icon: MagnifyingGlassIcon,
            content: searchItemsSection,
          },
        ]
      : []),
    ...(showMyScoreSection && tab && onOpenScoreModal && onApplySavedScore && onClearWeights
      ? [
          {
            id: 'my-score',
            title: 'My Score',
            icon: AdjustmentsHorizontalIcon,
            content: myScoreInlineContent,
          },
        ]
      : []),
    {
      id: 'sources',
      title: 'Sources',
      icon: CircleStackIcon,
      content: <SidebarSources tab={tab} source={source} sourceWeights={sourceWeights} />,
    },
    {
      id: 'columns',
      title: 'Columns',
      icon: ViewColumnsIcon,
      content: (
        <SidebarColumns
          view={view}
          tab={effectiveSidebarTab}
          source={source}
          isEvidence={isEvidence}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
        />
      ),
    },
    {
      id: 'favorites',
      title: 'Favorites',
      icon: StarIcon,
      headerBadge: favorites.length > 0 ? favorites.length : undefined,
      content: favoritesSection,
    },
    {
      id: 'saved-columns',
      title: 'Saved Columns',
      icon: BookmarkSquareIcon,
      headerBadge: savedColumns.length > 0 ? savedColumns.length : undefined,
      content: savedColumnsSection,
    },
  ];

  const SIDEBAR_WIDTH_PX = 340;
  const SIDEBAR_COLLAPSED_WIDTH_PX = 52;
  const SIDEBAR_FLYOUT_WIDTH_PX = 320;
  const SIDEBAR_WIDTH_PX_MOBILE = '100vw';
  const MOBILE_TOGGLE_BAR_WIDTH_PX = 16;
  const isMobileLayout = !isDesktop;
  const sidebarWidth = isDesktop ? SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX_MOBILE;
  const mobileClosedWidth = `${MOBILE_TOGGLE_BAR_WIDTH_PX}px`;
  const desktopCollapsedWidth = `${SIDEBAR_COLLAPSED_WIDTH_PX}px`;
  const isDesktopCollapsed = isDesktop && !isOpen;

  return (
    <RightAsideSidebarView
      isOpen={isOpen}
      isDesktop={isDesktop}
      sidebarWidth={sidebarWidth}
      mobileClosedWidth={mobileClosedWidth}
      desktopCollapsedWidth={desktopCollapsedWidth}
      isDesktopCollapsed={isDesktopCollapsed}
      isMobileLayout={isMobileLayout}
      setSidebarOpen={setSidebarOpen}
      onClose={onClose}
      closeButtonRef={closeButtonRef}
      searchItemsSection={searchItemsSection}
      showSearchItemsSection={searchItems.length > 0 || !!contextSetSearchItems}
      searchItemsCount={searchItems.length}
      searchItemsSectionExpandSignal={searchItemsSectionExpandSignal}
      showMyScoreSection={showMyScoreSection}
      demoTourActive={demoTourActive}
      tab={tab}
      onOpenScoreModal={onOpenScoreModal}
      onApplySavedScore={onApplySavedScore}
      onClearWeights={onClearWeights}
      myScoreInlineContent={myScoreInlineContent}
      sourcesHeaderBadge={sourcesHeaderBadge}
      isAuthenticated={isAuthenticated}
      view={view}
      navigate={navigate}
      source={source}
      sourceWeights={sourceWeights}
      columnVisibilityModel={columnVisibilityModel}
      columnPanelToggle={columnPanelToggle}
      shouldShowResetButton={shouldShowResetButton}
      onResetToDefault={onResetToDefault}
      effectiveSidebarTab={effectiveSidebarTab}
      isEvidence={isEvidence}
      setColumnVisibilityModel={setColumnVisibilityModel}
      columnOrder={columnOrder}
      setColumnOrder={setColumnOrder}
      favoritesSection={favoritesSection}
      favoritesCount={favorites.length}
      savedColumnsSection={savedColumnsSection}
      savedColumnsCount={savedColumns.length}
      collapsedSections={collapsedSections}
      activeCollapsedPanel={activeCollapsedPanel}
      pinnedCollapsedPanel={pinnedCollapsedPanel}
      clearCollapsedHoverCloseTimeout={clearCollapsedHoverCloseTimeout}
      scheduleCollapsedFlyoutClose={scheduleCollapsedFlyoutClose}
      setActiveCollapsedPanel={setActiveCollapsedPanel}
      setPinnedCollapsedPanel={setPinnedCollapsedPanel}
      sidebarFlyoutWidthPx={SIDEBAR_FLYOUT_WIDTH_PX}
      showViewSwitcher={showManageGraph && canViewAnalyticsGraphs}
      selectedView={selectedView}
      onViewChange={handleViewChange}
    />
  );
}
